import equal from 'deep-equal';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import SearchInput, { SearchOptionGroup } from '@components/fieldsNative/SearchInput';
import styles from '@components/forms/ExploreFilters.module.css';
import Form from '@components/forms/Form';
import FormCondition from '@components/forms/FormCondition';
import { EventTypeEnum, ExploreSectionsEnum } from '@typing/Enums';
import { ArticleContentTypeEnum, EmployerBenefitCategoryEnum } from '@typing/Generated';

type FormData = {
  articleContentType?: ArticleContentTypeEnum;
  category?: string | null;
  eventType?: EventTypeEnum;
  search?: string | null;
};

type Props = {
  categoriesToInclude: string[];
  exploreSection: ExploreSectionsEnum;
  filters: FormData;
  onChange: (filterValues: FormData) => void;
  onSearch?: (input: string) => Promise<SearchOptionGroup[]>;
};

const ExploreFilters = ({ categoriesToInclude, exploreSection, filters, onChange, onSearch }: Props) => {
  const intl = useIntl();

  const articleContentTypeParam = createEnumParam<ArticleContentTypeEnum>(Object.values(ArticleContentTypeEnum));
  const eventTypeParam = createEnumParam<EventTypeEnum>(Object.values(EventTypeEnum));

  const [query, setQuery] = useQueryParams({
    articleContentType: withDefault(
      { ...articleContentTypeParam, urlName: 'exploreContentType' },
      filters.articleContentType
    ),
    category: withDefault({ ...StringParam, urlName: 'exploreCategory' }, filters.category),
    eventType: withDefault({ ...eventTypeParam, urlName: 'exploreEventType' }, filters.eventType),
    search: withDefault({ ...StringParam, urlName: 'exploreSearch' }, filters.search)
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange'
  });

  const articleContentType = form.watch('articleContentType');
  const category = form.watch('category');
  const eventType = form.watch('eventType');
  const search = form.watch('search');

  useEffect(() => {
    const values = { articleContentType, category, eventType, search };

    if (!equal(values, filters)) {
      setQuery(values);
      onChange(values);
    }
  }, [articleContentType, category, eventType, filters, onChange, search, setQuery]);

  const articleContentTypeOptions = useMemo(() => {
    const values = [ArticleContentTypeEnum.text, ArticleContentTypeEnum.video, ArticleContentTypeEnum.webinar];

    const options = values.map(option => ({
      label: intl.formatMessage({ id: `models.explore.articleType.${option}` }),
      value: option
    }));
    return options;
  }, [intl]);

  const eventTypeOptions = useMemo(() => {
    const values = [EventTypeEnum.UPCOMING, EventTypeEnum.PAST];

    const options = values.map(option => ({
      label: intl.formatMessage({ id: `models.explore.eventType.${option}` }),
      value: option
    }));
    return options;
  }, [intl]);

  const categoryOptions = useMemo(() => {
    const categories = [...categoriesToInclude].sort();
    const options = categories.map(option => ({
      label: (Object.values(EmployerBenefitCategoryEnum) as string[]).includes(option)
        ? intl.formatMessage({ id: `models.employerBenefitCategory.${option}` })
        : option,
      value: option
    }));

    options.unshift({
      label: intl.formatMessage({ id: 'dictionary.all' }),
      value: ''
    });

    return options;
  }, [categoriesToInclude, intl]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
      <div className={styles.searchInput}>
        <SearchInput
          error={form.formState.errors.search?.message}
          initialValue={defaultValues.search}
          name="search"
          noSpacing
          onSearch={onSearch}
          placeholder={intl.formatMessage({ id: 'models.explore.search' })}
          setValue={v => form.setValue('search', v)}
        />
      </div>
      <FormCondition
        condition={exploreSection === ExploreSectionsEnum.ARTICLES}
        fieldName="articleContentType"
        form={form}
      >
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.articleContentType}
          name="articleContentType"
          noSpacing
          options={articleContentTypeOptions}
        />
      </FormCondition>
      <FormCondition condition={exploreSection === ExploreSectionsEnum.EVENTS} fieldName="eventType" form={form}>
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.eventType}
          name="eventType"
          noSpacing
          options={eventTypeOptions}
        />
      </FormCondition>
      <FormCondition condition={exploreSection === ExploreSectionsEnum.MARKETPLACE} fieldName="category" form={form}>
        <RadioButtonGroup<FormData>
          allowDeselection
          control={form.control}
          error={form.formState.errors.category}
          name="category"
          noSpacing
          options={categoryOptions}
        />
      </FormCondition>
    </Form>
  );
};

export default ExploreFilters;
